import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionReworkDetail } from './hooks/useConstructionReworkDetail';
import { ReworkStatusBadge } from './components/ReworkStatusBadge';
import { Card, Button, Tabs, Badge, Avatar, Alert } from '@/components/ui';

export default function ReworkDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reworkOrder, isLoading, updateRework, isUpdating } = useConstructionReworkDetail(id);

  if (isLoading) return <div>Đang tải...</div>;
  if (!reworkOrder) return <div>Không tìm thấy lệnh sửa lỗi</div>;

  const handleStatusUpdate = async (newStatus: string) => {
    await updateRework({ status_code: newStatus as any });
  };

  return (
    <PageDetailLayout
      title={reworkOrder.title}
      subtitle={reworkOrder.rework_code}
      breadcrumbs={[
        { label: 'Thi công' },
        { label: 'Sửa lỗi', href: '/construction/rework' },
        { label: reworkOrder.rework_code }
      ]}
      headerExtra={<ReworkStatusBadge status={reworkOrder.status_code} className="px-3 py-1 text-xs" />}
      actions={
        <div className="flex gap-2">
          {reworkOrder.status_code === 'assigned' && (
            <Button variant="primary" onClick={() => handleStatusUpdate('in_progress')} isLoading={isUpdating}>Bắt đầu sửa</Button>
          )}
          {reworkOrder.status_code === 'in_progress' && (
            <Button variant="success" onClick={() => handleStatusUpdate('reinspection_pending')} isLoading={isUpdating}>Hoàn thành & Gửi nghiệm thu</Button>
          )}
          <Button variant="outline" icon="ri-printer-line">In lệnh</Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
           <Card className="p-6">
             <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
               <i className="ri-information-line text-slate-400" />
               Thông tin chi tiết
             </h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
               <div className="space-y-1">
                 <p className="text-[10px] font-bold text-slate-400  tracking-wider">Lỗi phát sinh từ</p>
                 <Badge variant="custom" className="bg-slate-50 text-slate-700">{reworkOrder.source_type?.toUpperCase()}</Badge>
               </div>
               <div className="space-y-1">
                 <p className="text-[10px] font-bold text-slate-400  tracking-wider">Mức độ nghiêm trọng</p>
                 <Badge className={`${reworkOrder.severity_code === 'critical' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
                   {reworkOrder.severity_code?.toUpperCase()}
                 </Badge>
               </div>
               <div className="space-y-1">
                 <p className="text-[10px] font-bold text-slate-400  tracking-wider">Mô tả chi tiết lỗi</p>
                 <p className="text-sm text-slate-700 leading-relaxed">{reworkOrder.description}</p>
               </div>
               <div className="space-y-1">
                 <p className="text-[10px] font-bold text-slate-400  tracking-wider">Khu vực</p>
                 <p className="text-sm font-medium text-slate-900">{reworkOrder.zone?.name}</p>
               </div>
             </div>
           </Card>

           <Tabs
             tabs={[
               { id: 'photos', label: 'Hình ảnh hiện trạng', icon: 'ri-image-line' },
               { id: 'instructions', label: 'Hướng dẫn khắc phục', icon: 'ri-guide-line' },
               { id: 'materials', label: 'Vật tư sử dụng', icon: 'ri-box-3-line' },
               { id: 'history', label: 'Lịch sử xử lý', icon: 'ri-history-line' },
             ]}
           >
             <div className="py-8 text-center text-slate-400">
                <i className="ri-folder-open-line text-4xl mb-2 block opacity-20" />
                <p className="text-xs">Thông tin đang được cập nhật...</p>
             </div>
           </Tabs>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-6">Nhân sự & Đội ngũ</h4>
            <div className="space-y-4">
               <div className="flex items-center justify-between">
                 <div className="flex items-center gap-3">
                   <Avatar src={reworkOrder.assignee?.avatar_url} fallback={reworkOrder.assignee?.full_name?.charAt(0)} size="sm" />
                   <div>
                     <p className="text-xs font-bold text-slate-900">{reworkOrder.assignee?.full_name}</p>
                     <p className="text-[10px] text-slate-500">Người chịu trách nhiệm</p>
                   </div>
                 </div>
                 <Button variant="ghost" size="xs" icon="ri-mail-line" />
               </div>
               <div className="pt-4 border-t border-slate-100">
                 <p className="text-[10px] font-bold text-slate-400  mb-2">Đội thi công thực hiện</p>
                 <p className="text-xs font-medium text-slate-900">{reworkOrder.team?.name}</p>
               </div>
            </div>
          </Card>

          <Card className="p-6 bg-slate-50 border-slate-200">
            <h4 className="font-bold text-slate-900 mb-4">Chi phí khắc phục</h4>
            <div className="space-y-3">
               <div className="flex justify-between items-center">
                 <span className="text-xs text-slate-500">Dự kiến:</span>
                 <span className="text-xs font-bold text-slate-900">{new Intl.NumberFormat('vi-VN').format(reworkOrder.estimated_cost || 0)} VND</span>
               </div>
               <div className="flex justify-between items-center">
                 <span className="text-xs text-slate-500">Thực tế:</span>
                 <span className="text-xs font-black text-rose-600">{new Intl.NumberFormat('vi-VN').format(reworkOrder.actual_cost || 0)} VND</span>
               </div>
            </div>
            <Alert variant="info" className="mt-4 p-3 bg-white border-slate-100">
               <p className="text-[10px] leading-relaxed">Chi phí này sẽ được hạch toán vào mục <b>Rework Cost</b> của dự án.</p>
            </Alert>
          </Card>
        </div>
      </div>
    </PageDetailLayout>
  );
}
